/**
 * AIShield useTelemetryPolling Hook
 * Connects TelemetryPollingManager to React lifecycle.
 * Manages visibility pausing, manual refresh, query identity tracking, and unmount cleanup.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import type {
  BundleQueryParams,
  AdaptedDashboardBundle,
  PollingStatus,
} from "../types/telemetry.ts";
import {
  TelemetryPollingManager,
  computeQueryIdentity,
} from "../services/pollingManager.ts";
import type { PollingState } from "../services/pollingManager.ts";


export interface UseTelemetryPollingResult {
  data: AdaptedDashboardBundle | null;
  status: PollingStatus;
  error: Error | null;
  isLoading: boolean;
  isRefreshing: boolean;
  isStale: boolean;
  isEmpty: boolean;
  hasNewData: boolean;
  serverTime: string | null;
  queryIdentity: string;
  refresh: () => Promise<boolean>;
  applyPendingData: () => void;
}

export function useTelemetryPolling(
  query: BundleQueryParams = {},
  options: { enabled?: boolean; onAuthExpired?: () => void } = {}
): UseTelemetryPollingResult {
  const enabled = options.enabled ?? true;
  const onAuthExpired = options.onAuthExpired;
  const managerRef = useRef<TelemetryPollingManager | null>(null);
  const queryRef = useRef<BundleQueryParams>(query);
  queryRef.current = query;

  const queryIdentity = computeQueryIdentity(query);
  const prevIdentityRef = useRef<string>(queryIdentity);

  const [state, setState] = useState<PollingState>(() => ({
    status: enabled ? "loading" : "idle",
    data: null,
    error: null,
    hasNewData: false,
    serverTime: null,
    queryIdentity,
    query: { ...query },
  }));

  // Effect lifecycle: Setup creates a new manager instance and starts it if enabled;
  // cleanup destroys ONLY this setup's instance. Supports React.StrictMode remounting.
  useEffect(() => {
    if (!enabled) {
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
      return;
    }

    const isInitiallyHidden =
      typeof document !== "undefined" && document.visibilityState === "hidden";

    const manager = new TelemetryPollingManager({
      initialQuery: queryRef.current,
      autoStart: false,
      initiallyPaused: isInitiallyHidden,
      onAuthExpired,
    });
    managerRef.current = manager;

    const unsubscribe = manager.subscribe((nextState) => {
      setState(nextState);
    });

    // Start inside effect lifecycle (never in render)
    manager.start();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        manager.pause();
      } else if (document.visibilityState === "visible") {
        manager.resume();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      unsubscribe();
      manager.destroy();
      if (managerRef.current === manager) {
        managerRef.current = null;
      }
    };
  }, [enabled]);

  // Synchronize query updates to active manager
  useEffect(() => {
    if (prevIdentityRef.current !== queryIdentity) {
      prevIdentityRef.current = queryIdentity;
      if (managerRef.current) {
        managerRef.current.setQuery(query);
      }
    }
  }, [queryIdentity, query]);

  const refresh = useCallback(async (): Promise<boolean> => {
    if (managerRef.current) {
      return await managerRef.current.refresh();
    }
    return false;
  }, []);

  const applyPendingData = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.applyPendingData();
    }
  }, []);

  const isStaleQuery = enabled && state.queryIdentity !== queryIdentity;

  if (!enabled) {
    return {
      data: null,
      status: "idle",
      error: null,
      isLoading: false,
      isRefreshing: false,
      isStale: false,
      isEmpty: false,
      hasNewData: false,
      serverTime: null,
      queryIdentity,
      refresh,
      applyPendingData,
    };
  }

  return {
    data: isStaleQuery ? null : state.data,
    status: isStaleQuery ? "loading" : state.status,
    error: isStaleQuery ? null : state.error,
    isLoading: isStaleQuery ? true : state.status === "loading",
    isRefreshing: isStaleQuery ? false : state.status === "refreshing",
    isStale: isStaleQuery ? false : state.status === "stale",
    isEmpty: isStaleQuery ? false : state.status === "empty",
    hasNewData: isStaleQuery ? false : state.hasNewData,
    serverTime: isStaleQuery ? null : state.serverTime,
    queryIdentity,
    refresh,
    applyPendingData,
  };
}

